/**
 * App Configuration Helper
 * 
 * Reads configuration from runtime config (public/config.json, environment overrides,
 * and public/config.js variable overrides)
 * with fallback to routes.json and safe defaults.
 * 
 * Priority:
 * 1. Runtime config loaded from public/config.js (window.__CONFIG__)
 * 2. Runtime config loaded from public/config.json
 * 3. routes.json (bundled defaults)
 * 4. hardcoded defaults
 * 
 * Usage:
 * import { getBasePath, getApiBasePath } from './config/appConfig';
 */

import routesConfig from '../routes.json';
import { getConfigValue } from './runtimeConfig';

const DEV_BASE_PATH = '';
const PROD_BASE_PATH = '/dfo';
const DEV_API_BASE_PATH = '/api';   // matches setupProxy.js intercept path
const PROD_API_BASE_PATH = '/dfo/api';

const normalizeBasePath = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed === '/') {
    return '';
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, '');
};

const normalizeApiBasePath = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsedUrl = new URL(trimmed);

      // Same-host aliases (like www/non-www) should keep routing on the current origin.
      if (typeof window !== 'undefined' && window.location?.hostname) {
        const currentHost = window.location.hostname.toLowerCase();
        const configuredHost = parsedUrl.hostname.toLowerCase();
        const isSameHost =
          configuredHost === currentHost ||
          configuredHost === `www.${currentHost}` ||
          currentHost === `www.${configuredHost}`;

        if (isSameHost) {
          return (parsedUrl.pathname || '').replace(/\/+$/, '');
        }
      }
    } catch {
      // Fall back to the configured value when URL parsing fails.
    }

    return trimmed.replace(/\/+$/, '');
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, '');
};

/**
 * Get the base path for frontend routing
 * @returns {string} Base path (e.g., '/dfo')
 */
export const getBasePath = () => {
  const runtimeValue = normalizeBasePath(getConfigValue('basePath', ''));
  if (runtimeValue) {
    return runtimeValue;
  }

  if (process.env.NODE_ENV === 'development') {
    return DEV_BASE_PATH;
  }

  const routeFallback = normalizeBasePath(routesConfig.basePath || '');
  return routeFallback || PROD_BASE_PATH;
};

/**
 * Get the API base path
 * @returns {string} API base path (e.g., '/dfo/api')
 */
export const getApiBasePath = () => {
  const runtimeValue = normalizeApiBasePath(getConfigValue('apiBasePath', ''));
  if (runtimeValue) {
    return runtimeValue;
  }

  // Dev must use the proxy path — do not fall through to routes.json or prod default.
  if (process.env.NODE_ENV === 'development') {
    return DEV_API_BASE_PATH;
  }

  const routeFallback = normalizeApiBasePath(routesConfig.apiBasePath || '');
  if (routeFallback) {
    return routeFallback;
  }

  return PROD_API_BASE_PATH;
};

// Export config object for convenience
const appConfig = {
  get basePath() {
    return getBasePath();
  },
  get apiBasePath() {
    return getApiBasePath();
  }
};

export default appConfig;
